import { Layout } from 'antd';
import { useSetAtom } from 'jotai';
import pathToRegexp from 'path-to-regexp';
import { useEffect, useLayoutEffect, useState } from 'react';
import { type RouteProps, Redirect, Route, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { GlobalHeader } from 'components/GlobalHeader';
import { type BreadcrumbItem, Breadcrumbs } from 'components/ui';
import { breadcrumbTitleAtom } from 'utils/account/atoms';
import { SpacedContainer } from 'utils/layouts/containers';

import { ErrorBoundary } from '../ErrorBoundary';
import PageHeader, { type PageHeaderProps } from '../PageHeader';

import { getBreadcrumbTitle, getFallbackRedirectPath } from './utils';

const ContentContainer = styled.div`
  margin: 1rem 1rem 0;
  --page-header-layout-content-offset: 0px;
`;

const Title = styled.h1`
  display: inline;
  font-size: 1.5rem;
  font-weight: 500;
`;

interface GetTabFromRouteArgs {
  title: React.ReactNode;
  path: string;
  href?: string;
}

const getTabFromRoute = ({ title, path, href }: GetTabFromRouteArgs) => ({
  key: href || path,
  label: title,
});

interface GetActiveKeyArgs {
  pathname: string;
  routes: (RouteProps<string> & { href?: string })[];
}

const getActiveKey = ({ routes, pathname }: GetActiveKeyArgs) => {
  const route = routes.find(({ path }) => pathToRegexp(path as string, []).exec(pathname));

  return route ? route.href || route.path : null;
};

interface Props extends PageHeaderProps {
  breadcrumbList?: BreadcrumbItem[];
  children?: React.ReactNode;
  loading?: boolean;
  routes?: (RouteProps<string> & { hidden?: false })[];
  /**
   * Set to `false` while `routes` may still be missing entries gated behind an
   * async query or flag (e.g. an entitlement check).
   */
  routesReady?: boolean;
  title?: React.ReactNode;
  wrapperClassName?: string;
}

export function PageHeaderLayout({
  breadcrumbList,
  children,
  loading,
  onTabChange,
  routes,
  routesReady = true,
  tabActiveKey,
  tabList,
  title,
  wrapperClassName: _wrapperClassName,
  ...pageHeaderProps
}: Props) {
  const history = useHistory();
  const { pathname } = useLocation();
  // Held in state, not a ref, so the offset is measured on whichever container is mounted.
  // A ref read only on mount left a later container without the offset, at 0px.
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
  const setBreadcrumbTitle = useSetAtom(breadcrumbTitleAtom);

  useLayoutEffect(() => {
    if (!contentElement) {
      return;
    }

    const updateContentOffset = () => {
      const rect = contentElement.getBoundingClientRect();
      const marginTop = parseFloat(window.getComputedStyle(contentElement).marginTop || '0');
      const contentOffset = Math.max(0, rect.top - marginTop);

      contentElement.style.setProperty('--page-header-layout-content-offset', `${contentOffset}px`);
      window.dispatchEvent(new Event('page-header-layout:offset-change'));
    };

    updateContentOffset();
    window.addEventListener('resize', updateContentOffset);

    const observedElements: Element[] = [contentElement];
    const pageHeaderElement = document.getElementById('page-header-component');
    const globalHeaderElement = document.getElementById('global-header-component');

    if (pageHeaderElement) {
      observedElements.push(pageHeaderElement);
    }

    if (globalHeaderElement) {
      observedElements.push(globalHeaderElement);
    }

    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateContentOffset();
      });

      observedElements.forEach(element => resizeObserver?.observe(element));
    }

    return () => {
      window.removeEventListener('resize', updateContentOffset);
      resizeObserver?.disconnect();
    };
  }, [contentElement]);
  // Derive a stable string so inline breadcrumb arrays with the same titles do not
  // churn the atom (undefined → value) on every parent render.
  const breadcrumbTitle = getBreadcrumbTitle(breadcrumbList, loading);

  useEffect(() => {
    setBreadcrumbTitle(breadcrumbTitle);
    return () => setBreadcrumbTitle(undefined);
  }, [breadcrumbTitle, setBreadcrumbTitle]);

  const handleTabChange = (tab: string) => {
    if (onTabChange) {
      return onTabChange(tab);
    }

    // assume that if props.routes are passed in then every tab key is a path
    if (routes) {
      return history.push(tab);
    }
  };

  const activeTab = tabActiveKey || (routes && getActiveKey({ routes, pathname }));
  const tabs = tabList || (routes && routes.filter(({ hidden }) => !hidden).map(getTabFromRoute));

  const fallbackRedirectPath =
    !children && routes && routesReady ? getFallbackRedirectPath({ pathname, routes }) : undefined;

  const content =
    children ||
    (routes &&
      (fallbackRedirectPath ? (
        <Redirect to={fallbackRedirectPath} />
      ) : (
        routes.map(route => <Route key={route.path as string} {...route} />)
      )));

  const headerContent = (
    <>
      {title && (
        <SpacedContainer>
          <Title data-testid="header-layout-title" id="header-layout-title">
            {title}
          </Title>
        </SpacedContainer>
      )}
      {breadcrumbList && (
        <SpacedContainer>
          <Breadcrumbs data-testid="breadcrumbs" items={breadcrumbList} loading={loading} />
        </SpacedContainer>
      )}
    </>
  );

  return (
    <ErrorBoundary>
      <Layout>
        <GlobalHeader content={headerContent} />
        <Layout.Content>
          <PageHeader
            key="pageheader"
            onTabChange={handleTabChange}
            tabActiveKey={activeTab as string}
            tabList={tabs}
            {...pageHeaderProps}
          />
          <ContentContainer ref={setContentElement}>{content}</ContentContainer>
        </Layout.Content>
      </Layout>
    </ErrorBoundary>
  );
}
